import type { Chain } from "viem/chains";
import type { Vault, VaultKey } from "../vaults/config";

// Type for chain IDs supported by the vault configuration
export type ChainId = number | `${number}`;

// Type for the vault configuration response
export interface VaultConfigResponse {
  chains: { [key: string]: Chain };
  vaults: { [key: string]: Vault };
}

let configCache: VaultConfigResponse | null = null;

/**
 * Validates if a chain ID is supported by checking the vault configuration
 */
export async function isChainSupported(chainId: ChainId): Promise<boolean> {
  const config = await fetchVaultConfigs();
  return chainId.toString() in config.chains;
}

/**
 * Gets the numeric chain ID from any valid chain ID input
 */
export function toChainId(value: ChainId): number {
  return typeof value === "number" ? value : Number(value);
}

/**
 * Fetches vault configurations from the API
 * @returns Promise<VaultConfigResponse>
 */
async function fetchVaultConfigs(): Promise<VaultConfigResponse> {
  if (configCache) {
    return configCache;
  }

  try {
    const response = await fetch(
      "https://api.nucleusearn.io/prod/vault-config"
    );
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    const data: VaultConfigResponse = await response.json();
    configCache = data;
    return configCache;
  } catch (error) {
    console.error("Error in fetchVaultConfigs:", error);
    throw error;
  }
}

/**
 * Fetches configuration for a specific vault
 * @param vaultKey The vault key to fetch configuration for (can be a known VaultKey or a new vault key from the API)
 * @returns Promise<Vault>
 */
async function fetchVaultConfig(vaultKey: VaultKey | string): Promise<Vault> {
  const configs = await fetchVaultConfigs();
  const vault = configs.vaults[vaultKey];
  if (!vault) {
    throw new Error(`Vault config not found for key: ${vaultKey}`);
  }
  return vault;
}

/**
 * Gets a specific chain configuration
 * @param chainId The chain ID to fetch configuration for
 * @returns Promise<Chain>
 */
async function getChainConfig(chainId: ChainId): Promise<Chain> {
  const configs = await fetchVaultConfigs();
  const key = chainId.toString();
  const chain = configs.chains[key];
  if (!chain) {
    throw new Error(`Chain config not found for ID: ${chainId}`);
  }
  return chain;
}

/**
 * Clears the configuration cache
 */
function clearConfigCache(): void {
  configCache = null;
}

export {
  clearConfigCache,
  fetchVaultConfig,
  fetchVaultConfigs,
  getChainConfig,
};
